// Two Guys From Italy — Glendale — "buy a big pizza, get a smaller one free"
// specials. Two combo items are built from the Build Your Own Pizza product
// (so topping/add-on names and prices stay in sync with the menu) and
// injected at the top of the Coupons category by applyDiscount.js. Each item
// links (via `customPath`) to the step-by-step builder in SpecialBuilder.js,
// which enforces "free pizza has equal or fewer toppings".
//
// On/off switch: `twoGuysGlendalePizzaSpecials` in src/promoConfig.js.

export const SHOP_ID = 'twoguysfromitalyglendale'

export const SPECIALS = [
  {
    slug: '16-12',
    id: 'tgfi-glendale-special-16-12',
    big: {label: '16"', sizeId: '16" 10 Slices'},
    free: {label: '12"', sizeId: '12" 6 Slices'},
  },
  {
    slug: '18-14',
    id: 'tgfi-glendale-special-18-14',
    big: {label: '18"', sizeId: '18" 12 Slices'},
    free: {label: '14"', sizeId: '14" 8 Slices'},
  },
]

export function specialName (special) {
  return `${special.big.label} Pizza + FREE ${special.free.label} Pizza`
}

export function isPizzaSpecialsLive (shopId) {
  const {twoGuysGlendalePizzaSpecials} = require('../../promoConfig')
  return !!twoGuysGlendalePizzaSpecials && shopId === SHOP_ID
}

// modgroup ids are fixed so the builder can find each section
export function groupId (special, which, section) {
  return `${special.slug}-${which}-${section}` // e.g. "16-12-free-toppings"
}

function findBuildYourOwn (menu) {
  for (const subMenu of menu.subMenus || []) {
    for (const product of subMenu.products || []) {
      const name = ((product.name && product.name.en) || '').toLowerCase()
      if (name.indexOf('build your own pizza') !== -1) { return product }
    }
  }
  return null
}

function priceForSize ({price, priceIf}, sizeId) {
  if (Array.isArray(priceIf)) {
    const match = priceIf.find((p) => p.match === sizeId)
    return match ? Number(match.price) || 0 : 0
  }
  return Number(price) || 0
}

// customerPicks are keyed by modifier id across the whole product, so each
// pizza gets its own prefixed copy of every modifier.
function copyModifiers (modifiers, {prefix, sizeId, free}) {
  return modifiers.map((mod) => ({
    id: `${prefix}:${mod.id}`,
    name: (mod.name || '').trim(),
    price: free ? 0 : priceForSize(mod, sizeId),
  }))
}

function buildPizzaGroups (special, which, byo) {
  const {label, sizeId} = special[which]
  const isFree = (which === 'free')
  const prefix = `${special.slug}-${which}`
  const findGroup = (test) => (byo.modgroups || []).find(test)
  const toppings = findGroup((g) => g.pizzaSections || /topping/i.test(g.name || ''))
  const extras = findGroup((g) => g.id === 'Modifiers')
  const sauce = findGroup((g) => /sauce/i.test(g.id || ''))
  if (!toppings) { return null }

  const title = isFree ? `FREE ${label} Pizza` : `${label} Pizza`
  const groups = [{
    id: groupId(special, which, 'toppings'),
    name: `${title} — Toppings`,
    type: 'multiple',
    modifiers: copyModifiers(toppings.modifiers, {prefix, sizeId, free: isFree}),
  }]
  if (extras) {
    groups.push({
      id: groupId(special, which, 'extras'),
      name: `${title} — Extras`,
      type: 'multiple',
      modifiers: copyModifiers(extras.modifiers, {prefix, sizeId}),
    })
  }
  if (sauce) {
    groups.push({
      id: groupId(special, which, 'sauce'),
      name: `${title} — Sauce`,
      type: 'single',
      modifiers: copyModifiers(sauce.modifiers, {prefix, sizeId}),
    })
  }
  return groups
}

export function buildSpecialProducts (menu) {
  const byo = findBuildYourOwn(menu)
  if (!byo) { return [] }
  const sizeGroup = (byo.modgroups || [])[0] || {modifiers: []}

  return SPECIALS.map((special) => {
    const bigSize = sizeGroup.modifiers.find((m) => m.id === special.big.sizeId)
    const bigGroups = buildPizzaGroups(special, 'big', byo)
    const freeGroups = buildPizzaGroups(special, 'free', byo)
    if (!bigSize || !bigGroups || !freeGroups) { return null }
    return {
      id: special.id,
      name: {en: specialName(special)},
      price: String(bigSize.price),
      description: {en: `Build a ${special.big.label} pizza and get a ${special.free.label} pizza FREE with equal or fewer toppings.`},
      tags: 'tgfi-pizza-special',
      showModgroupNames: true, // cart labels each pizza's toppings/extras/sauce
      photo: byo.photo,
      customPath: `/specials/${special.slug}`,
      modgroups: [...bigGroups, ...freeGroups],
    }
  }).filter(Boolean)
}

// Called from applyDiscount: puts the specials at the top of the Coupons
// category. If Coupons is missing, the specials aren't shown.
export function prependPizzaSpecials (menu) {
  try {
    if (!menu || !menu.subMenus || !isPizzaSpecialsLive(menu.shopId)) { return menu }
    const products = buildSpecialProducts(menu)
    if (products.length === 0) { return menu }
    const couponsIndex = menu.subMenus.findIndex((s) => ((s.name && s.name.en) || '').trim() === 'Coupons')
    if (couponsIndex === -1) { return menu }
    const subMenus = menu.subMenus.map((subMenu, i) => {
      if (i !== couponsIndex) { return subMenu }
      const named = products.map((p) => Object.assign({}, p, {sub: subMenu.name.en}))
      return Object.assign({}, subMenu, {products: [...named, ...subMenu.products]})
    })
    return Object.assign({}, menu, {subMenus})
  } catch (e) {
    console.log(e)
    return menu
  }
}
